import { useMemo, useState } from "react"

import { useTranslation } from "react-i18next"

import { useProps } from "contexts/PropsContext"

import { getAllTimezones, getUtcOffset } from "util/date"

import { Modal } from "./Modal"

interface TimezoneModalProps {
  open: boolean
  onClose: () => void
}

export const TimezoneModal = ({ open, onClose }: TimezoneModalProps) => {
  const { t } = useTranslation("translation", { useSuspense: false })
  const { timezone, setTimezone } = useProps()

  const [tzSearch, setTzSearch] = useState("")

  const allTimezones = useMemo(() => getAllTimezones(), [])

  const timezoneOptions = useMemo(() => {
    return allTimezones.map((tz) => ({
      value: tz,
      label: tz.replace(/_/g, " "),
      offset: getUtcOffset(tz)
    }))
  }, [allTimezones])

  const filteredTimezones = useMemo(() => {
    if (!tzSearch) return timezoneOptions
    const lower = tzSearch.toLowerCase()
    return timezoneOptions.filter((tz) => tz.label.toLowerCase().includes(lower) || tz.offset.toLowerCase().includes(lower))
  }, [tzSearch, timezoneOptions])

  const currentOffset = useMemo(() => getUtcOffset(timezone), [timezone])

  const handleTimezoneChange = (tz: string) => {
    setTimezone(tz)
    onClose()
  }

  return (
    <Modal open={open} handler={onClose}>
      <div className="relative flex flex-col modal-box bg-white dark:bg-[#0C0C1A] text-black dark:text-white" style={{ minWidth: 480 }}>
        <h3 className="font-bold text-lg mb-1">{t("settings.timezone")}</h3>
        <p className="text-xs opacity-60 mb-3">
          {timezone.replace(/_/g, " ")} {currentOffset && `(${currentOffset})`}
        </p>
        <input
          type="text"
          className="input input-bordered w-full text-sm mb-3 bg-white dark:bg-[#1E1F2B] border-gray-300 dark:border-[rgba(255,255,255,0.1)] text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-white/40"
          placeholder={t("settings.searchTimezone") as string}
          value={tzSearch}
          onChange={(e) => setTzSearch(e.target.value)}
          autoFocus
        />
        <div className="overflow-y-auto rounded-lg border border-gray-200 dark:border-[rgba(255,255,255,0.1)]" style={{ maxHeight: 300 }}>
          {filteredTimezones.length === 0 ? (
            <div className="px-4 py-6 text-center text-sm opacity-50">{t("settings.noTimezoneFound")}</div>
          ) : (
            filteredTimezones.map((tz) => (
              <button
                key={tz.value}
                className={`flex items-center justify-between w-full px-3 py-2 text-left text-sm transition border-b last:border-b-0 ${
                  timezone === tz.value
                    ? "bg-[rgba(132,0,168,0.08)]"
                    : "hover:bg-gray-50 dark:hover:bg-[#2a2b39] border-gray-100 dark:border-[rgba(255,255,255,0.05)]"
                }`}
                onClick={() => handleTimezoneChange(tz.value)}
              >
                <span>{tz.label}</span>
                <span className="text-xs opacity-50 ml-2 shrink-0">{tz.offset}</span>
              </button>
            ))
          )}
        </div>
        <div className="modal-action">
          <button className="btn btn-ghost btn-sm" onClick={onClose}>
            {t("settings.done")}
          </button>
        </div>
      </div>
    </Modal>
  )
}
